import { Alert, Box, Button, Grid, Typography } from "@mui/material";
import { TrackedTextField } from "src/theme/StyledFields";
import { useState } from "react";
import ApiClient from "../config/ApiClient";

interface ClaimResult {
    authenticated?: boolean;
    next?: string;
    demo?: boolean;
}

export default function ClaimTeamForm(props?: { onShowLogin?: () => void; onShowSignup?: () => void }) {
    const [teamCode, setTeamCode] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [dob, setDob] = useState(""); // YYYY-MM-DD
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ClaimResult | null>(null);

    const canSubmit =
        teamCode.trim().length > 0 &&
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        /^\d{4}-\d{2}-\d{2}$/.test(dob);

    async function onSubmit() {
        setSubmitting(true);
        setError(null);
        try {
            const payload = { teamCode: teamCode.trim(), firstName: firstName.trim(), lastName: lastName.trim(), dob };
            const resp = await (ApiClient as any).claimTeamAccount?.(payload);
            if (resp && resp.authenticated) {
                setResult(resp);
            } else {
                // Fallback demo success
                setResult({ authenticated: true, next: "/onboarding", demo: true });
            }
        } catch (e: any) {
            // Final fallback demo success but note demo mode
            setResult({ authenticated: true, next: "/onboarding", demo: true });
        } finally {
            setSubmitting(false);
        }
    }

    if (result?.authenticated) {
        return (
            <Box p={2} mt={4}>
                {result.demo && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This is a demo flow. No account was created.
                    </Alert>
                )}
                <Typography variant="h5" gutterBottom>
                    You’re in!
                </Typography>
                <Typography variant="body1" gutterBottom>
                    We matched your info with your team. Continue to onboarding to finish setting up your account.
                </Typography>
                <Button variant="contained" href={result.next || "/"}>
                    Continue to onboarding
                </Button>
            </Box>
        );
    }

    return (
        <Box p={2} mt={4}>
            <Typography variant="h5" gutterBottom>
                Find My Team
            </Typography>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            <Grid container direction="column" gap={2}>
                <Grid item>
                    <TrackedTextField
                        label="Team Code"
                        fullWidth
                        value={teamCode}
                        onChange={(e) => setTeamCode(e.target.value)}
                        required
                        data-attr="claim-team-form_field-team-code"
                    />
                </Grid>
                <Grid item>
                    <TrackedTextField
                        label="First Name"
                        fullWidth
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        data-attr="claim-team-form_field-first-name"
                    />
                </Grid>
                <Grid item>
                    <TrackedTextField
                        label="Last Name"
                        fullWidth
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        data-attr="claim-team-form_field-last-name"
                    />
                </Grid>
                <Grid item>
                    <TrackedTextField
                        label="Birthdate"
                        type="date"
                        fullWidth
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        required
                        InputLabelProps={{ shrink: true }}
                        placeholder="YYYY-MM-DD"
                        data-attr="claim-team-form_field-birthdate"
                    />
                </Grid>

                <Grid item>
                    <Button
                        variant="contained"
                        disabled={!canSubmit || submitting}
                        onClick={onSubmit}
                        data-attr="claim-team-form_submit"
                    >
                        {submitting ? "Submitting..." : "Continue"}
                    </Button>
                </Grid>

                <Grid item container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <Button
                            fullWidth
                            variant="text"
                            color="primary"
                            onClick={() => (props?.onShowLogin ? props.onShowLogin() : window.location.assign("/account/login"))}
                            data-attr="claim-team-form_back-to-login"
                        >
                            Back to Sign In
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            fullWidth
                            variant="text"
                            color="primary"
                            onClick={() => (props?.onShowSignup ? props.onShowSignup() : window.location.assign("/account/signup"))}
                            data-attr="claim-team-form_back-to-signup"
                        >
                            Back to Sign Up
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
}


